function toDate(dateInput: any): Date | null {
  if (!dateInput) return null;
  if (typeof dateInput === 'object' && '_seconds' in dateInput) return new Date(dateInput._seconds * 1000);
  if (typeof dateInput === 'object' && 'seconds' in dateInput) return new Date(dateInput.seconds * 1000);
  const d = new Date(dateInput);
  return isNaN(d.getTime()) ? null : d;
}

// Locale-aware relative labels
const relativeLabels: Record<string, { justNow: string; m: string; h: string; d: string; w: string }> = {
  en: { justNow: 'just now', m: 'm ago', h: 'h ago', d: 'd ago', w: 'w ago' },
  it: { justNow: 'adesso', m: 'min fa', h: 'h fa', d: 'g fa', w: 'sett fa' },
};

let _currentLocale = 'en';

export const setDateLocale = (locale: string) => {
  _currentLocale = locale;
};

export const formatRelative = (dateInput: any): string => {
  const date = toDate(dateInput);
  if (!date) return '—';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  const labels = relativeLabels[_currentLocale] || relativeLabels.en;

  if (diffMins < 1) return labels.justNow;
  if (diffMins < 60) return `${diffMins}${labels.m}`;
  if (diffHours < 24) return `${diffHours}${labels.h}`;
  if (diffDays < 7) return `${diffDays}${labels.d}`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}${labels.w}`;
  
  const loc = _currentLocale === 'it' ? 'it-IT' : 'en-US';
  return date.toLocaleDateString(loc, { month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatDate = (dateStr: string): string => {
  const loc = _currentLocale === 'it' ? 'it-IT' : 'en-US';
  return new Date(dateStr).toLocaleDateString(loc, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatDateTime = (dateInput: any): string => {
  const date = toDate(dateInput);
  if (!date) return '—';
  const loc = _currentLocale === 'it' ? 'it-IT' : 'en-US';
  return `${date.toLocaleDateString(loc, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} ${_currentLocale === 'it' ? 'alle' : 'at'} ${date.toLocaleTimeString(loc, { hour: 'numeric', minute: '2-digit' })}`;
};

/** Full datetime for native `title` tooltips; undefined when the value is missing. */
export const dateTimeTooltip = (dateInput: any): string | undefined => {
  const label = formatDateTime(dateInput);
  return label === '—' ? undefined : label;
};

/**
 * Interview table: first line D/MM/YYYY, second line HH:mm (24h), same for Created and When.
 */
export const formatInterviewTableDateLines = (dateInput: any): { dateStr: string; timeStr: string } | null => {
  const date = toDate(dateInput);
  if (!date) return null;
  const day = date.getDate();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const dateStr = `${day}/${month}/${year}`;
  const timeStr = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return { dateStr, timeStr };
};
